import { useState, useEffect } from 'react'
import { apiService, User } from '../services/apiService'

const USER_ID_KEY = 'wordle_user_id'

export function useUser() {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    initializeUser()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const initializeUser = async () => {
    try {
      setLoading(true)
      setError(null)

      // Check if user ID exists in localStorage
      let userId = localStorage.getItem(USER_ID_KEY)
      let currentUser: User | null = null

      if (userId) {
        // Try to fetch existing user
        currentUser = await apiService.getUser(userId)
      }

      if (!currentUser) {
        // Create new user if none exists or fetch failed
        currentUser = await apiService.createUser()
        localStorage.setItem(USER_ID_KEY, currentUser.id)
      }

      setUser(currentUser)
    } catch (err) {
      console.error('Failed to initialize user:', err)
      
      // Fallback: create a local-only user for development
      const fallbackUserId = localStorage.getItem(USER_ID_KEY) || generateFallbackUserId()
      localStorage.setItem(USER_ID_KEY, fallbackUserId)
      
      // Generate a simple mnemonic for development
      const fallbackMnemonic = await generateFallbackMnemonic(fallbackUserId)
      
      setUser({
        id: fallbackUserId,
        mnemonic: fallbackMnemonic,
        created_at: new Date().toISOString()
      })
      
      setError('Running in offline mode - game data will not be saved to database')
    } finally {
      setLoading(false)
    }
  }

  const generateFallbackUserId = (): string => {
    return 'local-' + Math.random().toString(36).substr(2, 9) + '-' + Date.now().toString(36)
  }

  const generateFallbackMnemonic = async (userId: string): Promise<string> => {
    try {
      // Try to use the mnemonic generator if available
      const { twoWordMnemonic } = await import('../utils/mnemonicGenerator')
      return await twoWordMnemonic(userId)
    } catch {
      // Simple fallback if mnemonic generator fails
      const adjectives = ['Happy', 'Clever', 'Swift', 'Bright', 'Kind']
      const nouns = ['Cat', 'Dog', 'Bird', 'Fish', 'Tree']
      const hash = userId.split('').reduce((a, b) => { a = ((a << 5) - a) + b.charCodeAt(0); return a & a }, 0)
      const adjIndex = Math.abs(hash) % adjectives.length
      const nounIndex = Math.abs(Math.floor(hash / adjectives.length)) % nouns.length
      return adjectives[adjIndex] + nouns[nounIndex]
    }
  }

  const submitGameResult = async (
    wordSubmitted: string,
    attemptsCount: number,
    attemptWords: string[]
  ) => {
    if (!user) {
      throw new Error('No user available')
    }

    try {
      const submissionId = await apiService.submitGame({
        userId: user.id,
        wordSubmitted,
        attemptsCount,
        attemptWords,
      })

      return submissionId
    } catch (err) {
      console.error('Failed to submit game result:', err)
      
      // In development/offline mode, just log the game data
      if (user.id.startsWith('local-')) {
        console.log('Game completed (offline mode):', {
          userId: user.id,
          wordSubmitted,
          attemptsCount,
          attemptWords
        })
        return -1 // Return fake submission ID
      }
      
      throw err
    }
  }

  const getUserHistory = async () => {
    if (!user) {
      throw new Error('No user available')
    }

    try {
      return await apiService.getUserHistory(user.id)
    } catch (err) {
      console.error('Failed to fetch user history:', err)
      throw err
    }
  }

  return {
    user,
    loading,
    error,
    submitGameResult,
    getUserHistory,
    refreshUser: initializeUser,
  }
}